import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { baseurladdress } from "../baseurl";
import { useNavigate } from "react-router-dom";
import dpImage from '../img/hr-logo.jpg';
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';

const BILLING_REPORT_MENU = {
  id: 99001,
  name: "Billing Report",
  icon: "fa-chart-line",
  parent: 0,
  url: "billingreport",
};

const SESSION_REPORT_MENU = {
  id: 99002,
  name: "Session Report",
  icon: "fa-file-alt",
  parent: 0,
  url: "session-report",
};

const APPOINTMENT_REPORT_MENU = {
  id: 99003,
  name: "Appointment Report",
  icon: "fa-calendar-check",
  parent: 0,
  url: "appointment-report",
};

const DOCTOR_DETAILS_MENU = {
  id: 99010,
  name: "Doctor Details",
  icon: "fa-user-md",
  parent: 0,
  url: "doctors",
};

const DOCTOR_BULK_ASSIGN_MENU = {
  id: 99011,
  name: "Bulk Assign",
  icon: "fa-layer-group",
  parent: 0,
  url: "doctorbulkassign",
};

const REPORTS_PARENT_MENU = {
  id: 99020,
  name: "Reports",
  icon: "fa-chart-bar",
  parent: 0,
  url: null,
};

const DEFAULT_DOCTOR_PARENT_MENU = {
  id: 99009,
  name: "Doctors",
  icon: "fa-stethoscope",
  parent: 0,
  url: null,
};

const Sidennav = ({ isOpen, refresh, onCloseSidebar }) => {
  const [menus, setMenus] = useState([]);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menusLoaded, setMenusLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMenusByRole = async () => {
      try {
        const roleid = localStorage.getItem("roleid");
        const userid = localStorage.getItem("id");
        const accesstoken = localStorage.getItem("token");

        if (!roleid || !userid || !accesstoken) {
          console.warn("Missing credentials in localStorage.");
          return;
        }

        const response = await axios.get(
          `${baseurladdress}users/fetchprogrambasedonrole/${roleid}/${userid}`,
          {
            headers: { Authorization: `Bearer ${accesstoken}` },
          }
        );

        if (response.data.status === "Success") {
          const isAdmin = String(roleid) === "1";
          const data = response.data.data;
          const autoIncludeIds = [10, 11, 12, 13, 15, 16, 22, 23]; // IDs to exclude from sidebar display 
          // Filter out auto-include IDs for sidebar display
          const displayMenus = data.filter(
            p => !autoIncludeIds.includes(Number(p.pgm_id))
          );

          const formatted = displayMenus.map(p => ({
            id: Number(p.pgm_id),
            name: p.pgm_url?.toLowerCase() === "session-track" ? "Session Tracker" : p.pgm_menu,
            icon: p.pgm_icon,
            parent: Number(p.pgm_parent),
            url: p.pgm_url,
          }));

          if (!formatted.some(item => item.id === REPORTS_PARENT_MENU.id)) {
            formatted.push({ ...REPORTS_PARENT_MENU });
          }

          const reportChildren = [
            ...(isAdmin ? [{ ...BILLING_REPORT_MENU, parent: REPORTS_PARENT_MENU.id }] : []),
            { ...SESSION_REPORT_MENU, parent: REPORTS_PARENT_MENU.id },
            { ...APPOINTMENT_REPORT_MENU, parent: REPORTS_PARENT_MENU.id },
          ];

          reportChildren.forEach((child) => {
            if (!formatted.some(item => item.url?.toLowerCase() === child.url?.toLowerCase() && Number(item.parent) === Number(child.parent))) {
              formatted.push(child);
            }
          });

          const doctorParentSource = formatted.find(item => item.url?.toLowerCase() === "doctors" || item.name?.toLowerCase() === "doctors") || DEFAULT_DOCTOR_PARENT_MENU;
          const doctorParent = {
            ...doctorParentSource,
            url: null,
          };
          if (!formatted.some(item => item.id === doctorParent.id)) {
            formatted.push(doctorParent);
          }

          const doctorChildren = [
            { ...DOCTOR_DETAILS_MENU, parent: doctorParent.id },
            { ...DOCTOR_BULK_ASSIGN_MENU, parent: doctorParent.id },
          ];

          doctorChildren.forEach((child) => {
            if (!formatted.some(item => item.url?.toLowerCase() === child.url.toLowerCase() && Number(item.parent) === Number(child.parent))) {
              formatted.push(child);
            }
          });

          setMenus(formatted);
          setMenusLoaded(true);
        } else {
          setMenus([]);
          setMenusLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
        setMenus([]);
        setMenusLoaded(true);
      }
    };

    const fetchURLByRole = async () => {
      try {
        const roleid = localStorage.getItem("roleid");
        const userid = localStorage.getItem("id");
        const accesstoken = localStorage.getItem("token");

        if (!roleid || !userid || !accesstoken) {
          console.warn("Missing credentials in localStorage.");
          return;
        }

        const response = await axios.get(
          `${baseurladdress}users/fetchurlbasedonrole/${roleid}/${userid}`,
          {
            headers: { Authorization: `Bearer ${accesstoken}` },
          }
        );

        if (response.data.status === "Success") {
          const isAdmin = String(roleid) === "1";
          const data = response.data.data;

          // Get all allowed URLs (including auto-include IDs)
          const allowedUrls = data
            .map(p => p.pgm_url?.toLowerCase())
            .filter(Boolean);
          if (allowedUrls.includes("session-track") && !allowedUrls.includes("session-report")) {
            allowedUrls.push("session-report");
          }
          if (!allowedUrls.includes("appointment-report")) {
            allowedUrls.push("appointment-report");
          }
          if (!allowedUrls.includes("doctors")) {
            allowedUrls.push("doctors");
          }
          if (!allowedUrls.includes("doctorbulkassign")) {
            allowedUrls.push("doctorbulkassign");
          }
          if (isAdmin) {
            allowedUrls.push("billingreport");
          }

          const pathname = window.location.pathname;
          const segments = pathname.split("/").filter(Boolean);
          // const currentPath = segments[0]?.toLowerCase();
          const currentPath = segments.length > 1
            ? segments[1].toLowerCase()
            : segments[0]?.toLowerCase();
          // Just check if the current path is allowed
          const isAllowed = allowedUrls.includes(currentPath);

          if (!isAllowed && currentPath !== "unauthorized") {
            navigate("/unauthorized");
          }

          // You can now use isAllowed to show/hide UI elements or handle logic
          console.log(`Current path "${currentPath}" allowed:`, isAllowed);
        }
      } catch (error) {
        console.error("Error fetching URLs:", error);
      }
    };

    fetchURLByRole();
    fetchMenusByRole();
  }, [refresh, navigate]);

  // Helper function to close sidebar on mobile
  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 1024 && onCloseSidebar) {
      onCloseSidebar();
    }
  };

  const handleParentClick = (parent) => {
    const hasChildren = menus.some(child => child.parent === parent.id);

    if (hasChildren) {
      setExpandedMenu(expandedMenu === parent.id ? null : parent.id);
    } else if (parent.url) {
      setSelectedMenu(parent.id);
      localStorage.setItem("selectedMenu", parent.id);
      navigate(`/${parent.url}`);
      closeSidebarOnMobile(); // Close sidebar after navigation on mobile
    }
  };

  const handleChildClick = (child) => {
    setSelectedMenu(child.id);
    localStorage.setItem("selectedMenu", child.id);
    setExpandedMenu(child.parent);
    navigate(`/${child.url}`);
    closeSidebarOnMobile(); // Close sidebar after navigation on mobile
  };

  const parentMenus = menus.filter(menu => menu.parent === 0);

  return (
    <div className={`sidebar ${isOpen ? "open" : "collapsed"}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 0 16px',
          overflow: 'hidden',
          marginBottom: '8px'
        }}
      >
        <img
          src={dpImage}
          alt="DDC Logo"
          style={{
            width: isOpen ? '72px' : '48px',
            height: isOpen ? '72px' : '48px',
            objectFit: 'contain',
            flexShrink: 0,
            transition: 'all 0.3s ease'
          }}
        />
        {isOpen && (
          <span
            style={{
              fontSize: '22px',
              fontWeight: 800,
              color: 'white',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              marginTop: '10px'
            }}
          >
            DDC
          </span>
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: '0 10px', margin: 0 }} id="sidebarr">
        {parentMenus.map(parent => {
          const hasChildren = menus.some(child => child.parent === parent.id);
          const isParentActive =
            selectedMenu === parent.id ||
            menus.some(child => child.parent === parent.id && child.id === selectedMenu);

          return (
            <li
              key={parent.id}
              style={{
                marginBottom: '4px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '11px 12px',
                  cursor: hasChildren || parent.url ? 'pointer' : 'default',
                  backgroundColor: selectedMenu === parent.id ? '#1e40af' : 'transparent',
                  color: selectedMenu === parent.id ? 'white' : 'rgba(255,255,255,0.85)',
                  fontSize: '14px',
                  fontWeight: selectedMenu === parent.id ? 600 : 500,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  if (selectedMenu !== parent.id) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedMenu !== parent.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                onClick={() => handleParentClick(parent)}
                title={!isOpen ? parent.name : ''}
              >
                <i
                  className={`fa ${parent.icon}`}
                  style={{
                    marginRight: isOpen ? '12px' : '0',
                    width: '16px',
                    textAlign: 'center',
                    fontSize: '15px',
                    transition: 'margin 0.3s ease'
                  }}
                ></i>
                {isOpen && (
                  <>
                    <span style={{ flex: 1 }}>{parent.name}</span>
                    {hasChildren && (
                      <ChevronDown
                        size={15}
                        style={{
                          marginLeft: 'auto',
                          transform: expandedMenu === parent.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}
                      />
                    )}
                  </>
                )}
              </div>

              {isOpen && hasChildren && expandedMenu === parent.id && (
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '2px 0 0',
                  paddingLeft: '14px'
                }}>
                  {menus
                    .filter(child => child.parent === parent.id)
                    .map(child => {
                      const isChildActive = selectedMenu === child.id;
                      return (
                        <li
                          key={child.id}
                          style={{
                            marginBottom: '3px'
                          }}
                          onClick={() => handleChildClick(child)}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '9px 12px',
                              cursor: 'pointer',
                              backgroundColor: isChildActive ? '#1e40af' : 'transparent',
                              color: isChildActive ? 'white' : 'rgba(255,255,255,0.8)',
                              fontSize: '13.5px',
                              fontWeight: isChildActive ? 600 : 500,
                              transition: 'background-color 0.2s ease, color 0.2s ease',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              borderRadius: '8px'
                            }}
                            onMouseEnter={(e) => {
                              if (!isChildActive) {
                                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isChildActive) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                            title={child.name}
                          >
                            <i
                              className={`fa ${child.icon}`}
                              style={{
                                marginRight: '12px',
                                width: '16px',
                                textAlign: 'center',
                                fontSize: '13px'
                              }}
                            ></i>
                            <span>{child.name}</span>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Sidennav;